import { Controller, useFormContext } from 'react-hook-form-latest';
import Switch from '@/components/UI/FormElements/Switch';
import { ErrorMessage } from '@hookform/error-message';
import type { FormValues } from '../SingleSignOn';
import type { Saml2ProviderProps, SsoProviderResponse } from '../SingleSignOn.types';
import SamlMetadataFileUpload from './SamlMetadataFileUpload';
import type { UploadConfig } from './SamlMetadataFileUpload';
import { FC, useEffect, useState } from 'react';
import { ResponseType } from '@/support/LegacyApiService';
import Loading from '@/components/Loading';
import Banner from '@/components/UI/Banner';
import { _notNil } from '@/littledash';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook';

const Saml2: FC<Saml2ProviderProps> = ({ onMetadataFileChange, onLoadingChange, onSavedEnabledChange }) => {
  const {
    register,
    control,
    formState: { errors },
    setValue,
  } = useFormContext<FormValues>();
  const [uploadConfig, setUploadConfig] = useState<UploadConfig | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  const { response, loading, invoke } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'team.sso_providers',
    path: { type: 'saml2' },
    options: { onError: { toast: false, throw: false, capture: true, slug: 'saml2-sso-load-config' } },
  });

  const hasMetadataFile = _notNil(downloadUrl);

  useEffect(() => {
    if (onMetadataFileChange) {
      onMetadataFileChange(hasMetadataFile);
    }
  }, [hasMetadataFile, onMetadataFileChange]);

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(loading);
    }
  }, [loading, onLoadingChange]);

  useEffect(() => {
    if (response?.type === ResponseType.Success) {
      const body = response.body as SsoProviderResponse;
      const data = body?.data;
      if (_notNil(data) && data.type === 'saml2') {
        setValue('saml2', {
          enabled: data.enabled ?? false,
          audience: data.settings?.audience ?? '',
          issuer: data.settings?.issuer ?? '',
        });
        setUploadConfig(data.metadata_file_upload ?? null);
        setDownloadUrl(data.metadata_file_download_url ?? null);
        onSavedEnabledChange?.(data.enabled ?? false);
      }
    }
  }, [response, setValue, onSavedEnabledChange]);

  if (loading) {
    return (
      <div className="ph3 pv4">
        <Loading />
      </div>
    );
  }

  return (
    <div className="ph3">
      {!hasMetadataFile && (
        <Banner info className="mb3" dismiss={false}>
          <h3 className="f5 normal basier-med lh-title">Upload metadata XML file first</h3>
          <p className="f6 pt2 lh-copy">
            Please upload your identity provider&#39;s SAML metadata XML file before configuring your SSO settings.
          </p>
        </Banner>
      )}

      <div className="mb4 w-third">
        <SamlMetadataFileUpload
          uploadConfig={uploadConfig}
          downloadUrl={downloadUrl}
          onUploadComplete={async () => {
            await invoke();
          }}
        />
      </div>

      <hr className="bt-0 bl-0 br-0 bb b--moon-gray mv4" />

      <div className="mb3">
        <label>Enable SAML SSO</label>
        <Controller
          name="saml2.enabled"
          defaultValue={false}
          control={control}
          render={({ field: { value, onChange } }) => (
            <Switch disabled={!hasMetadataFile} value={value} onChange={onChange} />
          )}
        />
      </div>
      <div className="mb3">
        <label htmlFor="saml2.audience">Audience</label>
        <input
          {...register('saml2.audience', { required: 'This field is required' })}
          id="saml2.audience"
          type="text"
          disabled={!hasMetadataFile}
          style={{ marginBottom: 0 }}
        />
        <ErrorMessage
          errors={errors}
          name="saml2.audience"
          render={({ message }) => <p className="f6 red db pt2">{message}</p>}
        />
      </div>
      <div className="mb3">
        <label htmlFor="saml2.issuer">Issuer (Entity ID)</label>
        <input
          {...register('saml2.issuer', { required: 'This field is required' })}
          id="saml2.issuer"
          type="text"
          disabled={!hasMetadataFile}
          style={{ marginBottom: 0 }}
        />
        <ErrorMessage
          errors={errors}
          name="saml2.issuer"
          render={({ message }) => <p className="f6 red db pt2">{message}</p>}
        />
      </div>
    </div>
  );
};

export default Saml2;
